import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  Typography,
  Box,
  Stack,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

export interface FlagOption {
  _id: string;
  name: string;
  category: string;
  [key: string]: any;
}

export type FlagSelectionMode = "single" | "multi";

export interface FlagCategoryPickerProps {
  open: boolean;
  onClose: () => void;
  onSave: (selectedFlags: FlagOption[]) => void;
  flags: FlagOption[];
  value?: FlagOption[];
  selectionMode?: FlagSelectionMode | Record<string, FlagSelectionMode>;
  title?: string;
  categoryOrder?: string[];
  loading?: boolean;
  saveLabel?: string;
  allowClear?: boolean;
  uncategorizedHint?: string;
  /** z-index for the dialog. Defaults above the custom template drawer (1301). */
  zIndex?: number;
}

const EMPTY_FLAGS: FlagOption[] = [];
const UNCATEGORIZED = "Uncategorized";

// Brand accent (matches --ctpl-primary / --ctpl-primary-dark).
const ACCENT = "#f5a623";
const ACCENT_DARK = "#dc9015";
const ACCENT_SOFT = "#fff3dc";

// Gold-accent outlined input, matching the rest of the app.
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "14px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#e4e7ec" },
    "&:hover fieldset": { borderColor: ACCENT },
    "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "2px" },
  },
} as const;

// Popper renders above the dialog (dialog is at zIndex 1600).
const popperSx = { popper: { sx: { zIndex: 1700 } } } as const;

export default function FlagCategoryPicker({
  open,
  onClose,
  onSave,
  flags,
  value = EMPTY_FLAGS,
  selectionMode = "single",
  title = "Tag Flags",
  categoryOrder,
  loading = false,
  saveLabel = "Save",
  allowClear = true,
  uncategorizedHint = "These flags have no category set. Please update the flag's category in Manage Flags to make it selectable.",
  zIndex = 1600,
}: FlagCategoryPickerProps) {
  const grouped = React.useMemo(() => {
    const map: Record<string, FlagOption[]> = {};
    for (const f of flags || []) {
      const cat = f.category || UNCATEGORIZED;
      (map[cat] ||= []).push(f);
    }
    return map;
  }, [flags]);

  const categories = React.useMemo(() => {
    const keys = Object.keys(grouped);
    if (categoryOrder?.length) {
      const ordered = categoryOrder.filter((c) => keys.includes(c));
      const rest = keys.filter((c) => !categoryOrder.includes(c));
      return [...ordered, ...rest];
    }
    return keys;
  }, [grouped, categoryOrder]);

  const modeFor = React.useCallback(
    (category: string): FlagSelectionMode =>
      typeof selectionMode === "string"
        ? selectionMode
        : selectionMode[category] ?? "single",
    [selectionMode]
  );

  const [selection, setSelection] = React.useState<Record<string, FlagOption[]>>({});

  const valueKey = React.useMemo(
    () => (value || []).map((f) => f._id).sort().join(","),
    [value]
  );

  React.useEffect(() => {
    if (!open) return;
    const seed: Record<string, FlagOption[]> = {};
    for (const f of value || []) {
      const cat = f.category || UNCATEGORIZED;
      (seed[cat] ||= []).push(f);
    }
    setSelection(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, valueKey]);

  const handleCategoryChange = (category: string, next: FlagOption[]) => {
    setSelection((prev) => ({ ...prev, [category]: next }));
  };

  const handleClear = () => setSelection({});

  const handleSave = () => {
    const flat = categories.flatMap((c) => selection[c] || []);
    onSave(flat);
    onClose();
  };

  const totalSelected = categories.reduce(
    (sum, c) => sum + (selection[c]?.length || 0),
    0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow:
            "0 8px 24px rgba(16, 24, 40, .14), 0 2px 6px rgba(16, 24, 40, .08)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "16px",
          fontWeight: 700,
          color: "#1f2430",
          py: 2,
        }}
      >
        {title}
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "#e4e7ec" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: ACCENT }} />
          </Box>
        ) : categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No flags available.
          </Typography>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {categories.map((category) => {
              const options = grouped[category] || [];
              const mode = modeFor(category);
              const current = selection[category] || [];
              const isUncategorized = category === UNCATEGORIZED;

              const field =
                mode === "single" ? (
                  <Autocomplete
                    options={options}
                    disabled={isUncategorized}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={current[0] ?? null}
                    onChange={(_e, newVal) =>
                      handleCategoryChange(category, newVal ? [newVal] : [])
                    }
                    slotProps={popperSx}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props as any;
                      return (
                        <li key={option._id} {...rest}>
                          {option.name}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder={`Select ${category}`}
                        sx={fieldSx}
                      />
                    )}
                  />
                ) : (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    disabled={isUncategorized}
                    options={options}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={current}
                    onChange={(_e, newVal) => handleCategoryChange(category, newVal)}
                    slotProps={popperSx}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...rest } = props as any;
                      return (
                        <li key={option._id} {...rest}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8, color: ACCENT }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      );
                    }}
                    renderTags={(val, getTagProps) =>
                      val.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index }) as any;
                        return (
                          <Chip
                            key={option._id}
                            variant="outlined"
                            label={option.name}
                            {...tagProps}
                            sx={{
                              margin: "4px 2px",
                              backgroundColor: ACCENT_SOFT,
                              borderColor: "#f3d190",
                              color: "#92650d",
                              fontWeight: 600,
                            }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder={`Select ${category}`}
                        sx={fieldSx}
                      />
                    )}
                  />
                );

              return (
                <Box key={category}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 0.75,
                      fontWeight: 700,
                      color: "#1f2430",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {category}
                    {isUncategorized ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "warning.main" }}
                      >
                        (set a category to select)
                      </Typography>
                    ) : (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "#5b6472" }}
                      >
                        {mode === "single" ? "(select one)" : "(select multiple)"}
                      </Typography>
                    )}
                    {!isUncategorized && current.length > 0 && (
                      <Button
                        size="small"
                        onClick={() => handleCategoryChange(category, [])}
                        sx={{
                          ml: "auto",
                          minWidth: 0,
                          px: 1,
                          fontSize: 11,
                          color: "#5b6472",
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Typography>

                  {isUncategorized ? (
                    <Tooltip title={uncategorizedHint} arrow placement="top">
                      <span style={{ display: "block", width: "100%" }}>{field}</span>
                    </Tooltip>
                  ) : (
                    field
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {allowClear && (
          <Button
            onClick={handleClear}
            disabled={totalSelected === 0}
            sx={{ color: "#5b6472" }}
          >
            Clear all
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: "#5b6472" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: ACCENT,
            borderRadius: "9px",
            fontWeight: 600,
            textTransform: "none",
            px: 2.5,
            boxShadow: "none",
            "&:hover": { backgroundColor: ACCENT_DARK, boxShadow: "none" },
          }}
        >
          {saveLabel}
          {totalSelected > 0 ? ` (${totalSelected})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
}